// WP-3.1 (M1 PT-1..6, S-19/S-20): party queries/mutations.
import {
  partyListResponseSchema,
  partyProfileResponseSchema,
  partyResponseSchema,
  type PartyCreateRequest,
  type PartyEditRequest,
  type PartyMergeRequest,
  type PartyProfileResponse,
  type PartyResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const PARTIES_KEY = ['parties'] as const;

export function useParties(archived?: boolean): UseQueryResult<PartyResponse[]> {
  const qs = archived !== undefined ? `?archived=${String(archived)}` : '';
  return useQuery({
    queryKey: [...PARTIES_KEY, archived],
    queryFn: async () =>
      partyListResponseSchema.parse(await api.get(`/api/v1/parties${qs}`)).parties,
  });
}

export function useParty(id: string): UseQueryResult<PartyResponse> {
  return useQuery({
    queryKey: [...PARTIES_KEY, id],
    queryFn: async () => partyResponseSchema.parse(await api.get(`/api/v1/parties/${id}`)),
    enabled: id !== '',
  });
}

export function usePartyProfile(id: string): UseQueryResult<PartyProfileResponse> {
  return useQuery({
    queryKey: [...PARTIES_KEY, id, 'profile'],
    queryFn: async () =>
      partyProfileResponseSchema.parse(await api.get(`/api/v1/parties/${id}/profile`)),
    enabled: id !== '',
  });
}

function useInvalidateParties(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: PARTIES_KEY });
  };
}

export function useCreateParty(): UseMutationResult<PartyResponse, Error, PartyCreateRequest> {
  const invalidate = useInvalidateParties();
  return useMutation({
    mutationFn: async (input) =>
      partyResponseSchema.parse(await api.post('/api/v1/parties', input)),
    onSuccess: invalidate,
  });
}

export function useEditParty(
  id: string,
): UseMutationResult<PartyResponse, Error, PartyEditRequest> {
  const invalidate = useInvalidateParties();
  return useMutation({
    mutationFn: async (input) =>
      partyResponseSchema.parse(await api.patch(`/api/v1/parties/${id}`, input)),
    onSuccess: invalidate,
  });
}

export function useArchiveParty(): UseMutationResult<PartyResponse, Error, string> {
  const invalidate = useInvalidateParties();
  return useMutation({
    mutationFn: async (id) =>
      partyResponseSchema.parse(await api.post(`/api/v1/parties/${id}/archive`)),
    onSuccess: invalidate,
  });
}

export function useUnarchiveParty(): UseMutationResult<PartyResponse, Error, string> {
  const invalidate = useInvalidateParties();
  return useMutation({
    mutationFn: async (id) =>
      partyResponseSchema.parse(await api.post(`/api/v1/parties/${id}/unarchive`)),
    onSuccess: invalidate,
  });
}

export function useConfirmParty(): UseMutationResult<PartyResponse, Error, string> {
  const invalidate = useInvalidateParties();
  return useMutation({
    mutationFn: async (id) =>
      partyResponseSchema.parse(await api.post(`/api/v1/parties/${id}/confirm`)),
    onSuccess: invalidate,
  });
}

/** S-19 "Merge tool: pick two -> field-by-field keep-picker -> preview -> merge" (PT-4). */
export function useMergeParties(): UseMutationResult<PartyResponse, Error, PartyMergeRequest> {
  const invalidate = useInvalidateParties();
  return useMutation({
    mutationFn: async (input) =>
      partyResponseSchema.parse(await api.post('/api/v1/parties/merge', input)),
    onSuccess: invalidate,
  });
}
